import React, { useContext, useEffect } from "react";
import { format } from "date-fns";
import "./styles.css";

import {
  Behance,
  Dribbble,
  Github,
  Instagram,
  Linkedin,
  Location,
  Mail,
  Phone,
  Pinterest,
  Skype,
  Twitter,
  Website,
  Whatsapp,
  Laptop
} from "../../Icons/Icons";
import { ResumeContext } from "../../../contexts/resumeContext";

const iconMap = {
  linkedin: <Linkedin />,
  github: <Github />,
  dribbble: <Dribbble />,
  behance: <Behance />,
  instagram: <Instagram />,
  twitter: <Twitter />,
  pinterest: <Pinterest />,
  website: <Website />,
  skype: <Skype />,
  whatsapp: <Whatsapp />,
  custom: <Laptop />
};

function Rockstar({ info }) {
  const { setColorOptions, activeColor, setActiveColor, resumeHeadings, setImgSupported } =
    useContext(ResumeContext);

  useEffect(() => {
    setColorOptions(["#2465AB", "#466344", "#212642", "#423939"]);
    setActiveColor("#2465AB");
    setImgSupported(false)
  }, []);

  const {
    personalDetails,
    educationDetails,
    professionalDetails,
    skills,
    social,
    misc,
  } = info;

  const education = Object.values(educationDetails || {});
  const work = Object.values(professionalDetails || {});
  const userSkills = Object.values(skills || {});
  const socialLinks = Object.values(social || {});
  const { courses, hobbies, languages, references, internships, activities, projects } =
    misc || {};

  return (
    <div className={"rockstar"}>
      <div className={"rockstar__row"}>
        <div className={"rockstar__col"} style={{ width: "40%" }}>
          <h1 className={"rockstar__title"} style={{ color: activeColor }}>
            {personalDetails?.firstName?.trim()}{" "}
            {personalDetails?.lastName?.trim()}
          </h1>
          <p
            className={"rockstar__jobTitle"?.trim()}
            style={{ color: activeColor }}
          >
            {personalDetails?.jobTitle}
          </p>
          <div
            className={"rockstar__panel"}
            style={{ backgroundColor: activeColor }}
          >
            {personalDetails?.summary && <div className={"rockstar__panelSection"}>
              <h2 className={"rockstar__panelHeading"}>{resumeHeadings?.SUM}</h2>
              <p
                className={"rockstar__panelText"}
                dangerouslySetInnerHTML={{ __html: personalDetails?.summary }}
              />
            </div>}
            {userSkills?.length > 0 && <div
              className={`${"rockstar__panelSection"} ${"rockstar__skills"}`}
            >
              <h2 className={"rockstar__panelHeading"}>
                {resumeHeadings?.SK}
              </h2>
              <ul>
                {userSkills.map((skill, ix) => (
                  <li key={skill + ix}>{skill}</li>
                ))}
              </ul>
            </div>}
            <div className="rockstar__panelSection rockstar__contact">
              <h2 className={"rockstar__panelHeading"}>{resumeHeadings?.CL}</h2>
              <ul className={"rockstar__contactList"}>
                {personalDetails?.phone && (
                  <li>
                    <a href={`tel:${personalDetails?.phone?.trim()}`}>
                      <Phone />
                      {personalDetails?.phone?.trim()}
                    </a>
                  </li>
                )}
                {(personalDetails?.address?.trim() ||
                  personalDetails?.postalCode?.trim()) && (
                  <li>
                    <Location />
                    {personalDetails?.address?.trim()} 
                    {personalDetails?.postalCode?.trim() && " - " }
                    {personalDetails?.postalCode?.trim()}
                  </li>
                )}
                {personalDetails?.email && (
                  <li>
                    <a href={`mailto:${personalDetails?.email?.trim()}`}>
                      <Mail /> {personalDetails?.email?.trim()}
                    </a>
                  </li>
                )}
                {socialLinks.map((link, ix) => (
                  <li>
                    {iconMap[link.label?.toLowerCase()] || <>{link.label}: </>}{" "}
                    <a href={link.link} target="_blank">
                      {link.link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            {hobbies && (
              <div className={"rockstar__panelSection"}>
                <h2 className={"rockstar__panelHeading"}>{resumeHeadings?.HOB}</h2>
                <p className={"rockstar__content"}>{hobbies}</p>
              </div>
            )}
            {languages && (
              <div className={"rockstar__panelSection"}>
                <h2 className={"rockstar__panelHeading"}>{resumeHeadings?.LAN}</h2>
                <p className={"rockstar__content"}>{languages}</p>
              </div>
            )}
          </div>
        </div>
        <div className={"rockstar__col"} style={{ width: "55%" }}>
          {work?.length > 0 && <div className={"rockstar__section"}>
            <h2 className={"rockstar__heading"} style={{ color: activeColor }}>
              {resumeHeadings?.WE}
            </h2>
            {work.map((exp, ix) => (
              <div className={"rockstar__subsection"} key={`exp_${ix}`}>
                <h3 className={"rockstar__subheading"}>{exp.jobTitle}</h3>
                <p className={"rockstar__content"}>
                  {exp.company}
                  <span style={{ float: "right" }}>
                    {exp.startDate && (
                      <>{format(new Date(exp.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {exp.endDate && (
                     <>to {exp.endDate === "Present" ? "Present" : format(new Date(exp.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
                <div
                  className={"rockstar__content"}
                  dangerouslySetInnerHTML={{ __html: exp.description }}
                />
              </div>
            ))}
          </div>}
          {!!internships?.length && (
            <div className={"rockstar__section"}>
              <h2
                className={"rockstar__heading"}
                style={{ color: activeColor }}
              >
                {resumeHeadings?.INT}
              </h2>
              {internships.map((intern, ix) => (
                <div className={"rockstar__subsection"} key={`intern_${ix}`}>
                  <h3 className={"rockstar__subheading"}>{intern.jobTitle}</h3>
                  <p className={"rockstar__content"}>
                    {intern.company}
                    <span style={{ float: "right" }}>
                      {intern.startDate && (
                        <>{format(new Date(intern.startDate), "MMM yyyy")}</>
                      )}{" "}
                      {intern.endDate && (
                        <>to {format(new Date(intern.endDate), "MMM yyyy")}</>
                      )}
                    </span>
                  </p>
                  <div
                    className={"rockstar__content"}
                    dangerouslySetInnerHTML={{ __html: intern.description }}
                  />
                </div>
              ))}
            </div>
          )}
          {!!education?.length && <div className={"rockstar__section"}>
            <h2 className={"rockstar__heading"} style={{ color: activeColor }}>
              {resumeHeadings?.AD}
            </h2>
            {education?.map((ed, ix) => (
              <div className={"rockstar__subsection"} key={`ed_${ix}`}>
                <h3 className={"rockstar__subheading"}>
                  {ed?.institute} {ed?.location && <span>| {ed?.location}</span>}
                </h3>
                <p className={"rockstar__content"}>
                  {ed?.degree}{" "}
                  <span style={{ float: "right" }}>
                    {ed?.startDate && (
                      <>{format(new Date(ed?.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {ed?.endDate && (
                      <>to {format(new Date(ed?.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
                <div
                  className={"rockstar__content"}
                  dangerouslySetInnerHTML={{ __html: ed?.description }}
                />
              </div>
            ))}
          </div>}
          {!!projects?.length && <div className={"rockstar__section"}>
            <h2 className={"rockstar__heading"} style={{ color: activeColor }}>
              {resumeHeadings?.PRO}
            </h2>
            {projects?.map((pro, ix) => (
              <div className={"rockstar__subsection"} key={`pro_${ix}`}>
                <h3 className={"rockstar__subheading"}>
                  {pro?.title}
                </h3>
                <p className={"rockstar__content"}>
                  {pro?.company}{" "}
                  <span style={{ float: "right" }}>
                    {pro?.startDate && (
                      <>{format(new Date(pro?.startDate), "MMM yyyy")}</>
                    )}{" "}
                    {pro?.endDate && (
                      <>to {format(new Date(pro?.endDate), "MMM yyyy")}</>
                    )}
                  </span>
                </p>
                <div
                  className={"rockstar__content"}
                  dangerouslySetInnerHTML={{ __html: pro?.description }}
                />
              </div>
            ))}
          </div>}
          
          {!!courses?.length && (
            <div className={"rockstar__section"}>
              <h2
                className={"rockstar__heading"}
                style={{ color: activeColor }}
              >
                {resumeHeadings?.CR}
              </h2>
              {courses?.map((course, ix) => (
                <div className={"rockstar__subsection"} key={`course_${ix}`}>
                  <h3 className={"rockstar__subheading"}>{course.name}</h3>
                  <p className={"rockstar__content"}>
                    {course.institution}{" "}
                    <span style={{ float: "right" }}>
                      {course.startDate && (
                        <>{format(new Date(course.startDate), "MMM yyyy")}</>
                      )}{" "}
                      {course.endDate && (
                        <>to {format(new Date(course.endDate), "MMM yyyy")}</>
                      )}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}
          {!!activities?.length && (
            <div className={"rockstar__section"}>
              <h2
                className={"rockstar__heading"}
                style={{ color: activeColor }}
              >
                {resumeHeadings?.ACT}
              </h2>
              {activities.map((activity, ix) => (
                <div className={"rockstar__subsection"} key={`activity_${ix}`}>
                  <h3 className={"rockstar__subheading"}>{activity.title}</h3>
                  <p className={"rockstar__content"}>
                    {activity.institution}
                    <span style={{ float: "right" }}>
                      {activity.startDate && (
                        <>{format(new Date(activity.startDate), "MMM yyyy")}</>
                      )}{" "}
                      {activity.endDate && (
                        <>to {format(new Date(activity.endDate), "MMM yyyy")}</>
                      )}
                    </span>
                  </p>
                  <div
                    className={"rockstar__content"}
                    dangerouslySetInnerHTML={{ __html: activity.description }}
                  />
                </div>
              ))}
            </div>
          )}
          {!!references?.length && (
            <div className={"rockstar__section"}>
              <h2
                className={"rockstar__heading"}
                style={{ color: activeColor }}
              >
                {resumeHeadings?.REF}
              </h2>
              {references?.map((ref, ix) => (
                <div className={"rockstar__subsection"} key={`ref_${ix}`}>
                  <h3 className={"rockstar__subheading"}>
                    {ref.name} {ref.institution && <>from {ref.institution}</>}
                  </h3>
                  <p className={"rockstar__content"}>
                    {ref.email} {ref.phone && <> &nbsp; | &nbsp; {ref.phone}</>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Rockstar;
